import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Phone, PawPrint, MessageSquare, Loader2, Info } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { CONTACT, PET_TYPES, SERVICE_OPTIONS } from '../data/mock';
import { trackAppointmentSubmit, trackCallClick } from '../lib/analytics';

const TIME_SLOTS = [
  '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00',
];

const todayStr = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const BookAppointment = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    pet_name: '',
    pet_type: '',
    service: '',
    preferred_date: todayStr(),
    preferred_time: '',
    notes: '',
  });

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    if (form.name.trim().length < 2) return 'Please enter your name.';
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.mobile)) return 'Please enter a valid mobile number.';
    if (form.pet_name.trim().length < 1) return 'Please enter your pet\'s name.';
    if (!form.pet_type) return 'Please select pet type.';
    if (!form.service) return 'Please select a service.';
    if (!form.preferred_date) return 'Please select preferred date.';
    if (!form.preferred_time) return 'Please select preferred time.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast({ title: 'Please complete the form', description: err });
      return;
    }

    setSubmitting(true);
    try {
      const resp = await api.post('/appointments', form);
      trackAppointmentSubmit(form);
      // Navigate to Thank You page with request details
      navigate('/thank-you', {
        state: {
          request_id: resp?.data?.request_id,
          name: form.name,
          pet_name: form.pet_name,
          service: form.service,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
        },
      });
    } catch (e) {
      toast({
        title: 'Could not submit',
        description: e?.response?.data?.detail || 'Please try again or call us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Request An Appointment" breadcrumbs={[{ label: 'Request An Appointment' }]} />

      <section className="py-16 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-10">
          {/* Side info */}
          <aside className="space-y-6">
            <div className="bg-gradient-to-br from-[#5b3eb5] to-[#7c4ec9] rounded-2xl p-7 text-white shadow-xl">
              <PawPrint className="w-8 h-8 mb-3 text-[#f3d6f5]" />
              <h3 className="font-serif text-[24px] font-bold mb-3">Care that feels like family</h3>
              <p className="text-white/85 text-[15px] leading-relaxed">
                Tell us about your pet and we&rsquo;ll arrange a dedicated time slot with the right specialist.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm space-y-4">
              <div className="flex gap-3 items-start">
                <Phone className="w-5 h-5 text-[#5b3eb5] mt-1" />
                <div>
                  <p className="text-[13px] text-slate-500">Call us directly</p>
                  <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('book_sidebar')} className="font-semibold text-[#1a0b3d] hover:text-[#e870b6]">
                    {CONTACT.phone}
                  </a>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <Clock className="w-5 h-5 text-[#5b3eb5] mt-1" />
                <div>
                  <p className="text-[13px] text-slate-500">Hours</p>
                  <p className="font-semibold text-[#1a0b3d]">{CONTACT.hours}</p>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-7 lg:p-10 shadow-xl border border-slate-100"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={User} label="Pet Parent Name *">
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your full name" required className="h-12" />
              </Field>
              <Field icon={Phone} label="Mobile Number *">
                <Input type="tel" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} placeholder="+91 9XXXXXXXXX" required className="h-12" />
              </Field>
              <Field icon={PawPrint} label="Pet Name *">
                <Input value={form.pet_name} onChange={(e) => update('pet_name', e.target.value)} placeholder="e.g. Simba" required className="h-12" />
              </Field>
              <Field icon={PawPrint} label="Pet Type *">
                <Select value={form.pet_type} onValueChange={(v) => update('pet_type', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select pet type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PET_TYPES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field icon={PawPrint} label="Service Required *" className="sm:col-span-2">
                <Select value={form.service} onValueChange={(v) => update('service', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field icon={Calendar} label="Preferred Date *">
                <Input type="date" min={todayStr()} value={form.preferred_date} onChange={(e) => update('preferred_date', e.target.value)} required className="h-12" />
              </Field>
              <Field icon={Clock} label="Preferred Time *">
                <Select value={form.preferred_time} onValueChange={(v) => update('preferred_time', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field icon={MessageSquare} label="Additional Notes" className="sm:col-span-2">
                <Textarea
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                  placeholder="Anything we should know about your pet (allergies, medical history, behaviour, etc.)"
                  rows={4}
                />
              </Field>
            </div>

            <div className="mt-5 flex items-start gap-2 text-[13px] text-[#5b3eb5] bg-[#fdf3fc] border border-[#f3d6f5] rounded-xl px-4 py-3">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Your preferred appointment date and time will be confirmed by our team based on doctor and facility availability.</p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="mt-8 w-full sm:w-auto rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-10 py-7 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl transition-all disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><PawPrint size={16} /> Request Appointment</>}
            </Button>
            <p className="text-[12px] text-slate-500 mt-3">
              This is a request. We will call or message you to confirm the date and time.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ icon: Icon, label, children, className = '' }) => (
  <div className={className}>
    <Label className="flex items-center gap-2 text-[13px] font-semibold text-[#1a0b3d] mb-2">
      {Icon && <Icon size={14} className="text-[#5b3eb5]" />} {label}
    </Label>
    {children}
  </div>
);

export default BookAppointment;
